/**
 * 頂列右側：**營運要看的四個事實**，不是開發者開關。
 *
 * 從左到右＝一眼掃過的順序：
 *   ① 身分（現在用哪顆身分在回話）
 *   ② 分區（所在的一級區：工作台／房間／系統）
 *   ③ 未讀（0 件時也要看到「0」，才知道是真的沒信，不是沒載到）
 *   ④ 帳號池健康度（可用／總數；有登入失敗或憑證失效就轉警示色）
 *
 * 手機版只留 ①② 兩項的前兩項（身分＋未讀）：②分區與④健康度都是 `md:inline`，
 * 52.5px 的頂列不折行、不擠掉未讀。
 *
 * 開發者資訊降級：mock/live 徽章移到最右的角落、只在 mock（非 production）出現；
 * 版面密度留在最右，低調的小字按鈕，手機不顯示。密度仍在同一排但已不是視線第一落點，
 * 後續若要完全移出頂列，收進「設定」即可（`data-testid` 都保留）。
 *
 * 純呈現：所有狀態都由 `useConsoleActions` / `useDensity` 提供，這裡不留任何 state。
 * `data-testid` 沿用舊 App（inbox-counts／density-toggle／drawer-toggle／console-mode），
 * 因為視覺驗收與 e2e 都靠這幾個鉤子。
 */
import type { UseDensityResult } from '@chat-rea/console-ui';
import { Badge, Button, ThemeToggle } from '@tpte/ui';
import { densityToggleLabel } from '../shell-copy';

/**
 * 帳號池健康度。計數全部由呼叫端從 `PoolAccount`（`GET /api/accounts`）算好再傳，
 * 這裡不自己判斷狀態：`ok` / `tokenValid` 的語意只有一個地方定義（型別在 console-ui）。
 *
 * - `ok`：`ok === true` 的顆數（後端已驗過能登入）
 * - `failing`：`ok === false`（註冊了但登入失敗，還留在池子裡）
 * - `expired`：`tokenValid === false`（存著的憑證失效，要用時得重登）——可選，後端多半沒回
 */
export interface PoolHealth {
  total: number;
  ok: number;
  failing: number;
  expired?: number;
  /** 還沒載到池子（首次載入）時顯示「—」，不要謊報 0。 */
  loading?: boolean;
}

export interface TopbarActionsProps {
  conversationCount: number;
  unreadTotal: number;
  /** 沒傳就不出現第四項（保持既有呼叫端可用）。 */
  poolHealth?: PoolHealth;
  density: Pick<UseDensityResult, 'density' | 'isCompact' | 'toggle'>;
  /** 右側抽屜開關；不在「收件匣」這一頁時整個按鈕不出現。 */
  drawer?: { open: boolean; onToggle: () => void };
  mode: { isMock: boolean; title: string };
  /** 目前身分（沒有就顯示「未選身分」）：一人多身分時，這是最常被確認的一件事。 */
  identity?: { name: string; role?: string };
  /**
   * 登入者（auth-service）：**全站唯一一份身分顯示**。原本 AuthGate 有一顆 fixed 的
   * AccountChip 疊在頂列上，現在由這裡顯示 email／角色／登出，不再有第二份。
   */
  account?: { email: string; role: string };
  /** 登出（走 AuthGate 的既有流程：撤銷 PIN → 清 session 橋 → 回登入頁）。 */
  onSignOut?: () => void;
  /** 所在的一級分區（工作台／房間／系統）；頁名在頂列左側標題，這裡只放區。 */
  zone?: string;
}

/** 極細直線分隔：只分「資訊」與「動作」，不加框、不加陰影。 */
function Sep() {
  return <span aria-hidden className="hidden h-3.5 w-px flex-none bg-divider md:block" />;
}

export function TopbarActions({
  conversationCount,
  unreadTotal,
  poolHealth,
  density,
  drawer,
  mode,
  identity,
  account,
  onSignOut,
  zone,
}: TopbarActionsProps) {
  const hasIdentity = Boolean(identity && identity.name && identity.name !== '未選身分');
  /** 熱鍵 1–9 只在有對話時有意義：上限＝min(對話數, 9)，提示不謊報。 */
  const hotkeyMax = Math.min(conversationCount, 9);
  /**
   * 健康度＝「有沒有帳號現在不能用」。任一顆登入失敗、憑證失效，或整池沒有一顆可用（但池子不是空的），
   * 就轉警示色；載入中不算異常（還沒載到≠壞掉）。
   */
  const poolWarn = Boolean(
    poolHealth &&
      !poolHealth.loading &&
      (poolHealth.failing > 0 || (poolHealth.expired ?? 0) > 0 || (poolHealth.total > 0 && poolHealth.ok === 0)),
  );
  const poolTitle = poolHealth
    ? [
        `帳號池 ${poolHealth.ok}／${poolHealth.total} 可用`,
        poolHealth.failing > 0 ? `登入失敗 ${poolHealth.failing}` : null,
        (poolHealth.expired ?? 0) > 0 ? `憑證失效 ${poolHealth.expired}` : null,
      ]
        .filter(Boolean)
        .join('・')
    : '';
  return (
    <div className="flex flex-none items-center gap-2">
      {/* ① 身分：點＝有選身分（綠）／沒選（灰）。名稱可截斷，角色只在 md 以上。 */}
      <span
        className="flex min-w-0 max-w-[7.5rem] items-center gap-1.5 md:max-w-[13rem]"
        data-testid="topbar-identity"
        title={hasIdentity ? `${identity?.name}${identity?.role ? `（${identity.role}）` : ''}` : '尚未選身分'}
      >
        <span
          aria-hidden
          className={`size-1.5 flex-none rounded-full ${hasIdentity ? 'bg-success' : 'bg-text-disabled'}`}
        />
        <span className="truncate text-[12px] text-text-secondary">
          {hasIdentity ? identity?.name : '未選身分'}
        </span>
        {hasIdentity && identity?.role ? (
          <span className="hidden truncate font-mono text-[11px] tracking-pill text-text-tertiary md:inline">
            {identity.role}
          </span>
        ) : null}
      </span>

      {/* ② 分區：手機讓位給未讀（頂列只有 52.5px，不折兩行）。 */}
      {zone ? (
        <>
          <Sep />
          <span
            className="hidden font-mono text-[11px] tracking-pill text-text-tertiary md:inline"
            data-testid="topbar-zone"
            title="所在分區"
          >
            {zone}
          </span>
        </>
      ) : null}

      <Sep />

      {/* ③ 未讀：>0 才吃警告色；永遠顯示數字，避免「看不出是 0 還是沒載到」。 */}
      <Badge
        tone={unreadTotal > 0 ? 'warning' : 'neutral'}
        className="text-[12px]"
        data-testid="inbox-counts"
        title={`${conversationCount} 個對話・${unreadTotal} 則未讀`}
      >
        {unreadTotal} 未讀
      </Badge>

      {/* ④ 帳號池健康度：可用／總數。有壞的才吃警告色（平常是低權重的灰字）。
          沒傳 poolHealth 就整項不出現；手機（<md）不顯示，維持「身分＋未讀」兩項。 */}
      {poolHealth ? (
        <span
          className="hidden min-w-0 items-center gap-1.5 md:inline-flex"
          data-testid="topbar-pool-health"
          data-pool-warn={poolWarn ? 'true' : 'false'}
          title={poolTitle}
        >
          <span
            aria-hidden
            className={`size-1.5 flex-none rounded-full ${
              poolHealth.loading ? 'bg-text-disabled' : poolWarn ? 'bg-warning' : 'bg-success'
            }`}
          />
          <span
            className={`whitespace-nowrap font-mono text-[12px] tracking-pill ${
              poolWarn ? 'text-warning' : 'text-text-tertiary'
            }`}
          >
            {poolHealth.loading ? '池 —' : `池 ${poolHealth.ok}/${poolHealth.total}`}
          </span>
        </span>
      ) : null}

      {/* ⑥ 熱鍵提示：跟著**實際可用範圍**顯示（只有 N 個對話就只寫到 N），
          且只在 md 以上出現——手機沒有實體鍵盤，提示只是佔位。
          觸發方式只有一種：Alt/Ctrl/Cmd+數字；純數字不再切頁。 */}
      {conversationCount > 0 ? (
        <span
          className="hidden whitespace-nowrap rounded-md px-1.5 py-0.5 font-mono text-[11px] text-text-tertiary md:inline-flex"
          data-testid="hotkey-hint"
          title={`熱鍵（只在輸入框外生效）：Alt/⌘+1–${hotkeyMax} 切對話・Tab 跳未讀・/ 聚焦指令列・Esc 關面板`}
        >
          Alt+1–{hotkeyMax}
        </span>
      ) : null}
      {/* —— 以下為動作與開發者資訊，刻意放到最右、視覺權重最低 ── */}
      {drawer ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="hidden md:inline-flex"
          onClick={drawer.onToggle}
          aria-pressed={drawer.open}
          data-testid="drawer-toggle"
        >
          {drawer.open ? '面板 ▸' : '◂ 面板'}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="hidden font-mono text-[11px] text-text-tertiary lg:inline-flex"
        onClick={density.toggle}
        aria-pressed={density.isCompact}
        aria-label={densityToggleLabel(density.isCompact)}
        title={
          density.isCompact
            ? '目前是緊湊。按一下改成寬鬆。選擇記在這個瀏覽器。'
            : '目前是寬鬆。按一下改成緊湊。選擇記在這個瀏覽器。'
        }
        data-testid="density-toggle"
        data-density={density.density}
      >
        {densityToggleLabel(density.isCompact)}
      </Button>
      {/* mock 只在非 production 出現：正式站不需要讓營運看到「live」這種廢話。 */}
      {mode.isMock ? (
        <Badge tone="warning" className="hidden md:inline-flex" data-testid="console-mode" title={mode.title}>
          mock
        </Badge>
      ) : null}
      <ThemeToggle labels={{ light: 'Light', dark: 'Dark' }} labelPrefix="切換至" />

      {/* ⑤ 登入者：全站唯一一份身分顯示（AuthGate 的 fixed AccountChip 已移除，
          就是它疊在「帳號池」上面）。手機只留 email 截斷版，role 在 sm 以上才出現。 */}
      {account && account.email !== '' ? (
        <>
          <Sep />
          <span
            className="flex min-w-0 max-w-[6.5rem] items-center gap-1.5 md:max-w-[14rem]"
            data-testid="topbar-account"
            title={`${account.email}（${account.role}）`}
          >
            <span className="truncate font-mono text-[12px] text-text-secondary">{account.email}</span>
            <span className="hidden flex-none rounded-full bg-text-tertiary/15 px-2 py-0.5 font-mono text-[11px] text-text-secondary sm:inline">
              {account.role}
            </span>
          </span>
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              data-testid="sign-out"
              className="rounded-md border border-text-tertiary/30 px-2 py-0.5 text-[12px] text-text-secondary hover:border-text-secondary hover:text-text"
            >
              登出
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
